import { ReactNode } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { Sparkles, LucideIcon, CheckCircle2, ArrowRight } from "lucide-react";

interface SubscriptionRequiredProps {
  children: ReactNode;
  requiredPlans: ('enterprise')[];
  title?: string;
  description?: string;
  icon?: LucideIcon;
  featureList?: string[];
}

export function SubscriptionRequired({
  children,
  requiredPlans,
  title = 'Enterprise Feature',
  description = 'This feature requires an enterprise subscription.',
  icon: Icon = Sparkles,
  featureList = [],
}: SubscriptionRequiredProps) {
  const { subscription, isLoading } = useSubscription();
  const navigate = useNavigate();

  // Allow access if user has enterprise plan and subscription is active
  const hasAccess = !isLoading && 
    subscription &&
    requiredPlans.includes(subscription.plan as 'enterprise') && 
    ['active', 'trialing'].includes(subscription.status);

  // If user has access or loading, render children
  if (hasAccess || isLoading) {
    return <>{children}</>;
  }

  // Enterprise-only model
  const getButtonText = () => {
    if (subscription?.plan === 'enterprise') return 'Manage Subscription';
    return 'Contact Sales';
  };

  const getUpgradeUrl = () => {
    return '/pricing';
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-8">
      <Card className="w-full max-w-4xl mx-auto shadow-lg border-slate-200/60 bg-white/95 backdrop-blur-sm rounded-2xl">
        <CardHeader className="text-center pb-6 border-b border-slate-100">
          <div className="mx-auto bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-2xl mb-6 shadow-lg ring-2 ring-white/50">
            <Icon className="h-10 w-10 text-indigo-600" />
          </div>
          <CardTitle className="text-3xl font-bold text-slate-900 tracking-tight">{title}</CardTitle>
          <CardDescription className="text-lg mt-3 text-slate-600 leading-relaxed">{description}</CardDescription>
        </CardHeader>
        <CardContent className="pt-8 pb-6">
          {featureList.length > 0 && (
            <div className="space-y-2 mb-8">
              <h3 className="font-semibold text-lg mb-6 text-slate-900">With an enterprise subscription, you can:</h3>
              <ul className="space-y-4">
                {featureList.map((feature, index) => (
                  <li key={index} className="flex items-start group">
                    <div className="flex-shrink-0 h-6 w-6 bg-green-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-green-200 transition-colors duration-200">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-slate-700 leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-6 pt-2 pb-8 bg-slate-50/50 rounded-b-2xl">
          <Button 
            onClick={() => navigate(getUpgradeUrl())} 
            size="lg"
            className="w-full sm:w-auto sm:min-w-[240px] mx-auto bg-slate-900 hover:bg-slate-800 text-white rounded-xl py-3 px-8 font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <ArrowRight className="mr-3 h-5 w-5" />
            {getButtonText()}
          </Button>
          <p className="text-sm text-center text-slate-500 mt-4 leading-relaxed">
            Contact us to access enterprise features and supercharge your recruiting process.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
} 